/**
 * GadsKpi.js — GADS KPI-Seite (Freiseinapp Admin).
 * 14 ein-/ausblendbare Sektionen B01–B14 (Bezeichnungen aus den b-Kommandos).
 * Jede Sektion = Scroll-Bereich mit sichtbarem/klebrigem Header.
 * B01 gefüllt: Wochen- (Fr, letzte 7 T) + Monats-Berichte je Kampagne (Spalten = CSV-Export).
 * B02 gefüllt: reine IS-Bilanz je Woche (Fr–Do) + Monat/Kampagne (IS · Lost-Rang · Lost-Budget)
 *   via API-Fenster-Aggregat ab 01.01.2026. NUR eigene Daten — Konkurrenz-Domains bleiben der
 *   manuelle Import auf der Ads-Performance-Seite. IS=9,99 → Googles Maske „< 10 %" (masked-Flag,
 *   ehrlich angezeigt). oberste/obere bewusst NICHT hier (Dublette zu B01).
 * B03 gefüllt: Gerätebericht — je Gerät mit Traffic (Smartphones/Computer/Tablets; 0 Impr → keine
 *   Zeile) je Kampagne × Woche (Fr–Do) + Monat ab 01.01.2026. Additiv aus Tagestabelle aggregiert,
 *   oberste/obere per Fenster-Pull, Gebotsanpassung als aktuelles Setting.
 * B04 gefüllt: Altersdemografie — nur aktive Altersgruppen mit Traffic je Kampagne × Woche (Fr–Do)
 *   + Monat ab 01.01.2026. Additiv aus Tagestabelle, Gebotsanpassung als Setting. KEINE Anteils-
 *   metriken (API liefert IS/Lost-Rang/exact-match auf AGE_RANGE nicht).
 * B05 gefüllt: Standortbericht (Bundesland) — nur Standorte mit Traffic je Kampagne × Woche (Fr–Do)
 *   + Monat ab 01.01.2026. Additiv aus Tagestabelle ads_locations, Gebotsanpassung als Setting.
 *   KEINE oberste/obere (API liefert sie auf Location-Ebene nicht).
 * B06 gefüllt: Werbezeitplan — Tag × Daypart (6 Dayparts) mit Traffic je Kampagne × Woche (Fr–Do)
 *   + Monat ab 01.01.2026. Aus stündlicher Tagestabelle in Dayparts aggregiert, Gebotsanpassung
 *   als Setting. Conv-Wert/Alle-Conv nicht enthalten (=Conv).
 * B07 gefüllt: Zielgruppensegmente (Beobachtung) je Kampagne, Filter Klicks>0 (=CSV), einklappbar.
 *   Additiv aus Tagestabelle, Gebot uservertical-aufgelöst. Namen EN, kein Typ/keine Anteilsmetriken
 *   (API-Grenze).
 * B08 gefüllt: Suchbegriffe je Kampagne, Filter Status „Keines" (unbearbeitet), wöchentlich (Fr–Do)
 *   ab 01.01, einklappbar, MIT oberste/obere (Shares auf Search-Term-Ebene verfügbar). Direkt aus
 *   search_term_view (Tagestabelle hatte Status kaputt). GroupedReport-Flags shares/gebot.
 * B09 gefüllt: Keywords je Kampagne (ENABLED, negative=FALSE, inkl. 0-Impr wegen Max CPC), Wochen
 *   (Fr–Do) + Monate ab 01.01, einklappbar. Max CPC/QS/QS-Komponenten/Gesch. Gebote via extraCols
 *   (GroupedReport-Erweiterung). B10–B14 Platzhalter.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './GadsKpi.css';

const API_BASE = '/api';

const BEREICHE = [
  { key: 'b01', label: 'B01 · Kampagne' },
  { key: 'b02', label: 'B02 · Auktionen' },
  { key: 'b03', label: 'B03 · Geräte' },
  { key: 'b04', label: 'B04 · Alter' },
  { key: 'b05', label: 'B05 · Standorte' },
  { key: 'b06', label: 'B06 · Werbezeitplan' },
  { key: 'b07', label: 'B07 · Zielgruppen' },
  { key: 'b08', label: 'B08 · Suchbegriffe' },
  { key: 'b09', label: 'B09 · Keywords' },
  { key: 'b10', label: 'B10 · Titel' },
  { key: 'b11', label: 'B11 · Texte' },
  { key: 'b12', label: 'B12 · Sitelinks' },
  { key: 'b12a', label: 'B12a · Marke (Logo + Firma)' },
  { key: 'b12c', label: 'B12c · Bilder' },
  { key: 'b13', label: 'B13 · Zusammenfassung' },
  { key: 'b14', label: 'B14 · User-Intent' },
];

const KAMPAGNEN = [
  { code: 'VA', full: 'Verlustangst' },
  { code: 'EF', full: 'Eifersucht' },
  { code: 'EA', full: 'Emot. Abh.' },
  { code: 'SO', full: 'Souveränität' },
  { code: 'BR', full: 'Branding' },
];

const eur = n => (n == null ? '—' : Number(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €');
const pct = n => (n == null ? '—' : Number(n).toLocaleString('de-DE', { maximumFractionDigits: 2 }) + ' %');
const num = n => (n == null ? '—' : Number(n).toLocaleString('de-DE'));

export default function GadsKpi() {
  const [open, setOpen] = useState({ b01: true });
  const toggle = k => setOpen(o => ({ ...o, [k]: !o[k] }));
  const [selectedK, setSelectedK] = useState(Object.fromEntries(KAMPAGNEN.map(k => [k.code, true])));
  const toggleK = c => setSelectedK(s => ({ ...s, [c]: !s[c] }));
  const setAll = v => setSelectedK(Object.fromEntries(KAMPAGNEN.map(k => [k.code, v])));

  return (
    <div className="gk-wrap">
      <Link to="/admin" className="gk-back">← Zurück zum Dashboard</Link>
      <h2 className="gk-title">📊 GADS KPI <small>Kampagnen-Berichte B01–B14</small></h2>

      <div className="gk-kfilter">
        <span className="gk-kfilter-lbl">Kampagnen:</span>
        {KAMPAGNEN.map(k => (
          <label key={k.code} className={'gk-kchk' + (selectedK[k.code] ? ' on' : '')}>
            <input type="checkbox" checked={!!selectedK[k.code]} onChange={() => toggleK(k.code)} />
            <span>{k.code} <small>{k.full}</small></span>
          </label>
        ))}
        <button className="gk-kbtn" onClick={() => setAll(true)}>Alle an</button>
        <button className="gk-kbtn" onClick={() => setAll(false)}>Alle ab</button>
      </div>

      {BEREICHE.map(b => (
        <section className="gk-section" key={b.key}>
          <button className="gk-head" onClick={() => toggle(b.key)} aria-expanded={!!open[b.key]}>
            <span>{b.label}</span>
            <span className="gk-caret">{open[b.key] ? '▾' : '▸'}</span>
          </button>
          {open[b.key] && (
            <div className="gk-body">
              {b.key === 'b01' ? <B01Kampagne selectedK={selectedK} />
                : b.key === 'b02' ? <B02Auktionen selectedK={selectedK} />
                : b.key === 'b03' ? <B03Geraete selectedK={selectedK} />
                : b.key === 'b04' ? <B04Alter selectedK={selectedK} />
                : b.key === 'b05' ? <B05Standorte selectedK={selectedK} />
                : b.key === 'b06' ? <B06Zeitplan selectedK={selectedK} />
                : b.key === 'b07' ? <B07Zielgruppen selectedK={selectedK} />
                : b.key === 'b08' ? <B08Suchbegriffe selectedK={selectedK} />
                : b.key === 'b09' ? <B09Keywords selectedK={selectedK} />
                : b.key === 'b10' ? <B10Titel selectedK={selectedK} />
                : b.key === 'b11' ? <B11Texte selectedK={selectedK} />
                : b.key === 'b12' ? <B12Sitelinks selectedK={selectedK} />
                : b.key === 'b12a' ? <B12aMarke selectedK={selectedK} />
                : b.key === 'b12c' ? <B12cBilder selectedK={selectedK} />
                : <div className="gk-placeholder">— Bericht folgt —</div>}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}

// ---------- B01 · Kampagne ----------
function B01Kampagne({ selectedK }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('token') || '';
    fetch(`${API_BASE}/gads_kpi_b01.php`, { headers: { 'Authorization': 'Bearer ' + token } })
      .then(r => r.json())
      .then(j => { if (j.success) setRows(j.rows); else setErr(j.message || 'Fehler'); })
      .catch(e => setErr(String(e)));
  }, []);

  if (err) return <div className="gk-err">Fehler: {err}</div>;
  if (!rows) return <div className="gk-placeholder">lädt…</div>;
  const shown = rows.filter(r => !selectedK || selectedK[r.campaign]);
  if (!shown.length) return <div className="gk-placeholder">keine Kampagne ausgewählt (oder keine Daten)</div>;

  const rowKey = r => `${r.typ}|${r.zeitraum}|${r.campaign}`;
  const toggleRow = k => setSel(s => ({ ...s, [k]: !s[k] }));
  const allChecked = shown.every(r => sel[rowKey(r)]);
  const toggleAll = () => { const v = !allChecked; const n = { ...sel }; shown.forEach(r => { n[rowKey(r)] = v; }); setSel(n); };
  const pickedCount = shown.filter(r => sel[rowKey(r)]).length;

  const exportCsv = () => {
    const cols = [
      ['Typ', 'typ', 0], ['Zeitraum', 'zeitraum', 0], ['Kampagne', 'campaign', 0],
      ['Kosten', 'costs', 1], ['Impressionen', 'impr', 1], ['Klicks', 'clicks', 1], ['CTR %', 'ctr', 1], ['Ø CPC', 'cpc', 1],
      ['Conversions', 'conv', 1], ['Kosten/Conv.', 'cpa', 1], ['Conv.-Rate %', 'convrate', 1],
      ['Impr. oberste %', 'oberste', 1], ['Impr. obere %', 'obere', 1], ['IS %', 'is', 1], ['Lost Rang %', 'lost_rang', 1], ['Lost Budget %', 'lost_budget', 1],
      ['Aktive Keywords', 'active_keywords', 1], ['Strategie', 'strat', 0], ['Budget', 'budget', 1],
    ];
    const picked = shown.filter(r => sel[rowKey(r)]);
    if (!picked.length) { alert('Keine Zeile ausgewählt.'); return; }
    const cell = (r, c) => { const v = r[c[1]]; if (v == null) return ''; const s = c[2] ? String(v).replace('.', ',') : String(v); return /[";\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
    let csv = '﻿' + cols.map(c => c[0]).join(';') + '\n';
    picked.forEach(r => { csv += cols.map(c => cell(r, c)).join(';') + '\n'; });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = 'gads_b01.csv'; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
  };

  return (
    <>
    <div className="gk-toolbar">
      <button className="gk-kbtn" onClick={exportCsv} disabled={!pickedCount}>⬇ CSV-Export{pickedCount ? ` (${pickedCount})` : ''}</button>
      <span className="gk-toolbar-hint">Zeilen links per Checkbox wählen</span>
    </div>
    <div className="gk-tablewrap">
      <table className="gk-table">
        <thead>
          <tr>
            <th className="gk-chkcol"><input type="checkbox" checked={allChecked} onChange={toggleAll} title="alle" /></th>
            <th>Typ</th><th>Zeitraum</th><th>K</th>
            <th className="r">Kosten</th><th className="r">Impr.</th><th className="r">Klicks</th>
            <th className="r">CTR</th><th className="r">Ø CPC</th><th className="r">Conv.</th>
            <th className="r">Kosten/Conv.</th><th className="r">Conv.-Rate</th>
            <th className="r">oberste %</th><th className="r">obere %</th>
            <th className="r">IS</th><th className="r">Lost Rang</th><th className="r">Lost Budget</th>
            <th className="r">akt. KW</th><th>Strategie</th><th className="r">Budget</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((r, i) => (
            <tr key={i} className={(r.typ === 'Monat' ? 'gk-monat' : '') + (sel[rowKey(r)] ? ' gk-selected' : '')}>
              <td className="gk-chkcol"><input type="checkbox" checked={!!sel[rowKey(r)]} onChange={() => toggleRow(rowKey(r))} /></td>
              <td><span className={'gk-badge ' + (r.typ === 'Monat' ? 'gk-b-mon' : 'gk-b-wk')}>{r.typ}</span></td>
              <td className="gk-nowrap">{r.zeitraum}</td>
              <td><strong>{r.campaign}</strong></td>
              <td className="r">{eur(r.costs)}</td>
              <td className="r">{num(r.impr)}</td>
              <td className="r">{num(r.clicks)}</td>
              <td className="r">{pct(r.ctr)}</td>
              <td className="r">{eur(r.cpc)}</td>
              <td className="r">{num(r.conv)}</td>
              <td className="r">{eur(r.cpa)}</td>
              <td className="r">{pct(r.convrate)}</td>
              <td className="r">{pct(r.oberste)}</td>
              <td className="r">{pct(r.obere)}</td>
              <td className="r">{pct(r.is)}</td>
              <td className="r">{pct(r.lost_rang)}</td>
              <td className="r">{pct(r.lost_budget)}</td>
              <td className="r">{num(r.active_keywords)}</td>
              <td className="gk-nowrap">{r.strat}</td>
              <td className="r">{eur(r.budget)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="gk-note">Alle Werte direkt via GAds-API pro Zeitraum gezogen (ads_kpi_b01) — CSV-verifiziert exakt, inkl. oberste/obere % + aktive Keywords.</div>
    </div>
    </>
  );
}

// ---------- B02 · Auktionen (eigene Position) ----------
function B02Auktionen({ selectedK }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('token') || '';
    fetch(`${API_BASE}/gads_kpi_b02.php`, { headers: { 'Authorization': 'Bearer ' + token } })
      .then(r => r.json())
      .then(j => { if (j.success) setRows(j.rows); else setErr(j.message || 'Fehler'); })
      .catch(e => setErr(String(e)));
  }, []);

  if (err) return <div className="gk-err">Fehler: {err}</div>;
  if (!rows) return <div className="gk-placeholder">lädt…</div>;
  const shown = rows.filter(r => !selectedK || selectedK[r.campaign]);
  if (!shown.length) return <div className="gk-placeholder">keine Kampagne ausgewählt (oder keine Daten)</div>;

  const rowKey = r => `${r.typ}|${r.zeitraum}|${r.campaign}`;
  const toggleRow = k => setSel(s => ({ ...s, [k]: !s[k] }));
  const allChecked = shown.every(r => sel[rowKey(r)]);
  const toggleAll = () => { const v = !allChecked; const n = { ...sel }; shown.forEach(r => { n[rowKey(r)] = v; }); setSel(n); };
  const pickedCount = shown.filter(r => sel[rowKey(r)]).length;

  const exportCsv = () => {
    const cols = [
      ['Typ', 'typ', 0], ['Zeitraum', 'zeitraum', 0], ['Kampagne', 'campaign', 0], ['Impressionen', 'impr', 1],
      ['IS %', 'is', 1], ['Lost Rang %', 'lost_rang', 1], ['Lost Budget %', 'lost_budget', 1],
    ];
    const picked = shown.filter(r => sel[rowKey(r)]);
    if (!picked.length) { alert('Keine Zeile ausgewählt.'); return; }
    const cell = (r, c) => {
      if (c[1] === 'is' && r.masked) return '< 10'; // Googles Maske ehrlich mit ausgeben
      const v = r[c[1]]; if (v == null) return ''; const s = c[2] ? String(v).replace('.', ',') : String(v); return /[";\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    let csv = '﻿' + cols.map(c => c[0]).join(';') + '\n';
    picked.forEach(r => { csv += cols.map(c => cell(r, c)).join(';') + '\n'; });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = 'gads_b02_auktionen.csv'; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
  };

  return (
    <>
    <div className="gk-toolbar">
      <button className="gk-kbtn" onClick={exportCsv} disabled={!pickedCount}>⬇ CSV-Export{pickedCount ? ` (${pickedCount})` : ''}</button>
      <span className="gk-toolbar-hint">Nur eigene Auktions-Position (IS-Bilanz) — Zeilen links wählen</span>
    </div>
    <div className="gk-tablewrap">
      <table className="gk-table">
        <thead>
          <tr>
            <th className="gk-chkcol"><input type="checkbox" checked={allChecked} onChange={toggleAll} title="alle" /></th>
            <th>Typ</th><th>Zeitraum</th><th>K</th>
            <th className="r">Impr.</th>
            <th className="r">IS</th><th className="r">Lost Rang</th><th className="r">Lost Budget</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((r, i) => (
            <tr key={i} className={(r.typ === 'Monat' ? 'gk-monat' : '') + (sel[rowKey(r)] ? ' gk-selected' : '')}>
              <td className="gk-chkcol"><input type="checkbox" checked={!!sel[rowKey(r)]} onChange={() => toggleRow(rowKey(r))} /></td>
              <td><span className={'gk-badge ' + (r.typ === 'Monat' ? 'gk-b-mon' : 'gk-b-wk')}>{r.typ}</span></td>
              <td className="gk-nowrap">{r.zeitraum}</td>
              <td><strong>{r.campaign}</strong></td>
              <td className="r">{num(r.impr)}</td>
              <td className="r">{r.masked ? <span className="gk-muted" title="Google maskiert IS unter 10 % — kein exakter Wert">&lt; 10 %</span> : pct(r.is)}</td>
              <td className="r">{pct(r.lost_rang)}</td>
              <td className="r">{pct(r.lost_budget)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="gk-note">
        Eigene Auktions-Position via GAds-API-Fenster ab 01.01.2026 — Wochen (Fr–Do, wie B01) + Monate. IS + Lost-Rang + Lost-Budget = 100 % (vollständige Impression-Share-Bilanz), deckt sich mit „GADS letzte 7 Tage" (freitags).<br />
        <strong>„&lt; 10 %"</strong> = Googles Maske (IS unter 10 % gibt Google nicht exakt aus). oberste/obere Position siehe B01 (gleiche API-Metrik, keine Dublette). Konkurrenz-Domains: manueller Import auf der Ads-Performance-Seite.
      </div>
    </div>
    </>
  );
}

// ---------- B03 · Geräte ----------
const gb = g => (g == null ? '—' : (g > 0 ? '+' : '') + g + ' %'); // Gebotsanpassung

function B03Geraete({ selectedK }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('token') || '';
    fetch(`${API_BASE}/gads_kpi_b03.php`, { headers: { 'Authorization': 'Bearer ' + token } })
      .then(r => r.json())
      .then(j => { if (j.success) setRows(j.rows); else setErr(j.message || 'Fehler'); })
      .catch(e => setErr(String(e)));
  }, []);

  if (err) return <div className="gk-err">Fehler: {err}</div>;
  if (!rows) return <div className="gk-placeholder">lädt…</div>;
  const shown = rows.filter(r => !selectedK || selectedK[r.campaign]);
  if (!shown.length) return <div className="gk-placeholder">keine Kampagne ausgewählt (oder keine Daten)</div>;

  const rowKey = r => `${r.typ}|${r.zeitraum}|${r.campaign}|${r.device}`;
  const toggleRow = k => setSel(s => ({ ...s, [k]: !s[k] }));
  const allChecked = shown.every(r => sel[rowKey(r)]);
  const toggleAll = () => { const v = !allChecked; const n = { ...sel }; shown.forEach(r => { n[rowKey(r)] = v; }); setSel(n); };
  const pickedCount = shown.filter(r => sel[rowKey(r)]).length;

  const exportCsv = () => {
    const cols = [
      ['Typ', 'typ', 0], ['Zeitraum', 'zeitraum', 0], ['Kampagne', 'campaign', 0], ['Gerät', 'device', 0],
      ['Kosten', 'costs', 1], ['Impressionen', 'impr', 1], ['Klicks', 'clicks', 1], ['CTR %', 'ctr', 1], ['Ø CPC', 'cpc', 1],
      ['Conversions', 'conv', 1], ['Kosten/Conv.', 'cpa', 1], ['Conv.-Rate %', 'convrate', 1],
      ['oberste %', 'oberste', 1], ['obere %', 'obere', 1], ['Gebotsanp.', 'gebot', 0],
    ];
    const picked = shown.filter(r => sel[rowKey(r)]);
    if (!picked.length) { alert('Keine Zeile ausgewählt.'); return; }
    const cell = (r, c) => {
      if (c[1] === 'gebot') return r.gebot == null ? '' : (r.gebot > 0 ? '+' : '') + r.gebot;
      const v = r[c[1]]; if (v == null) return ''; const s = c[2] ? String(v).replace('.', ',') : String(v); return /[";\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    let csv = '﻿' + cols.map(c => c[0]).join(';') + '\n';
    picked.forEach(r => { csv += cols.map(c => cell(r, c)).join(';') + '\n'; });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = 'gads_b03_geraete.csv'; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
  };

  return (
    <>
    <div className="gk-toolbar">
      <button className="gk-kbtn" onClick={exportCsv} disabled={!pickedCount}>⬇ CSV-Export{pickedCount ? ` (${pickedCount})` : ''}</button>
      <span className="gk-toolbar-hint">Geräte je Kampagne (nur mit Traffic) — Zeilen links wählen</span>
    </div>
    <div className="gk-tablewrap">
      <table className="gk-table">
        <thead>
          <tr>
            <th className="gk-chkcol"><input type="checkbox" checked={allChecked} onChange={toggleAll} title="alle" /></th>
            <th>Typ</th><th>Zeitraum</th><th>K</th><th>Gerät</th>
            <th className="r">Kosten</th><th className="r">Impr.</th><th className="r">Klicks</th>
            <th className="r">CTR</th><th className="r">Ø CPC</th><th className="r">Conv.</th>
            <th className="r">Kosten/Conv.</th><th className="r">Conv.-Rate</th>
            <th className="r">oberste %</th><th className="r">obere %</th><th className="r">Gebot</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((r, i) => (
            <tr key={i} className={(r.typ === 'Monat' ? 'gk-monat' : '') + (sel[rowKey(r)] ? ' gk-selected' : '')}>
              <td className="gk-chkcol"><input type="checkbox" checked={!!sel[rowKey(r)]} onChange={() => toggleRow(rowKey(r))} /></td>
              <td><span className={'gk-badge ' + (r.typ === 'Monat' ? 'gk-b-mon' : 'gk-b-wk')}>{r.typ}</span></td>
              <td className="gk-nowrap">{r.zeitraum}</td>
              <td><strong>{r.campaign}</strong></td>
              <td className="gk-nowrap">{r.device}</td>
              <td className="r">{eur(r.costs)}</td>
              <td className="r">{num(r.impr)}</td>
              <td className="r">{num(r.clicks)}</td>
              <td className="r">{pct(r.ctr)}</td>
              <td className="r">{eur(r.cpc)}</td>
              <td className="r">{num(r.conv)}</td>
              <td className="r">{eur(r.cpa)}</td>
              <td className="r">{pct(r.convrate)}</td>
              <td className="r">{pct(r.oberste)}</td>
              <td className="r">{pct(r.obere)}</td>
              <td className="r">{gb(r.gebot)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="gk-note">
        Gerätebericht je Kampagne — eine Zeile je Gerät mit Traffic (Smartphones/Computer/Tablets; 0 Impressionen → keine Zeile), Wochen (Fr–Do) + Monate ab 01.01.2026. Additive Werte aus der Tagestabelle (30 T rollierend) aggregiert, Verhältnisse daraus neu gerechnet; oberste/obere % per API-Fenster-Pull (CSV-verifiziert); <strong>Gebot</strong> = aktuelle Geräte-Gebotsanpassung („—" = keine gesetzt).
      </div>
    </div>
    </>
  );
}

// ---------- B04 · Alter ----------
function B04Alter({ selectedK }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('token') || '';
    fetch(`${API_BASE}/gads_kpi_b04.php`, { headers: { 'Authorization': 'Bearer ' + token } })
      .then(r => r.json())
      .then(j => { if (j.success) setRows(j.rows); else setErr(j.message || 'Fehler'); })
      .catch(e => setErr(String(e)));
  }, []);

  if (err) return <div className="gk-err">Fehler: {err}</div>;
  if (!rows) return <div className="gk-placeholder">lädt…</div>;
  const shown = rows.filter(r => !selectedK || selectedK[r.campaign]);
  if (!shown.length) return <div className="gk-placeholder">keine Kampagne ausgewählt (oder keine Daten)</div>;

  const rowKey = r => `${r.typ}|${r.zeitraum}|${r.campaign}|${r.age}`;
  const toggleRow = k => setSel(s => ({ ...s, [k]: !s[k] }));
  const allChecked = shown.every(r => sel[rowKey(r)]);
  const toggleAll = () => { const v = !allChecked; const n = { ...sel }; shown.forEach(r => { n[rowKey(r)] = v; }); setSel(n); };
  const pickedCount = shown.filter(r => sel[rowKey(r)]).length;

  const exportCsv = () => {
    const cols = [
      ['Typ', 'typ', 0], ['Zeitraum', 'zeitraum', 0], ['Kampagne', 'campaign', 0], ['Alter', 'age', 0],
      ['Kosten', 'costs', 1], ['Impressionen', 'impr', 1], ['Klicks', 'clicks', 1], ['CTR %', 'ctr', 1], ['Ø CPC', 'cpc', 1],
      ['Conversions', 'conv', 1], ['Kosten/Conv.', 'cpa', 1], ['Conv.-Rate %', 'convrate', 1], ['Gebotsanp.', 'gebot', 0],
    ];
    const picked = shown.filter(r => sel[rowKey(r)]);
    if (!picked.length) { alert('Keine Zeile ausgewählt.'); return; }
    const cell = (r, c) => {
      if (c[1] === 'gebot') return r.gebot == null ? '' : (r.gebot > 0 ? '+' : '') + r.gebot;
      const v = r[c[1]]; if (v == null) return ''; const s = c[2] ? String(v).replace('.', ',') : String(v); return /[";\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    let csv = '﻿' + cols.map(c => c[0]).join(';') + '\n';
    picked.forEach(r => { csv += cols.map(c => cell(r, c)).join(';') + '\n'; });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = 'gads_b04_alter.csv'; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
  };

  return (
    <>
    <div className="gk-toolbar">
      <button className="gk-kbtn" onClick={exportCsv} disabled={!pickedCount}>⬇ CSV-Export{pickedCount ? ` (${pickedCount})` : ''}</button>
      <span className="gk-toolbar-hint">Nur aktive Altersgruppen mit Traffic — Zeilen links wählen</span>
    </div>
    <div className="gk-tablewrap">
      <table className="gk-table">
        <thead>
          <tr>
            <th className="gk-chkcol"><input type="checkbox" checked={allChecked} onChange={toggleAll} title="alle" /></th>
            <th>Typ</th><th>Zeitraum</th><th>K</th><th>Alter</th>
            <th className="r">Kosten</th><th className="r">Impr.</th><th className="r">Klicks</th>
            <th className="r">CTR</th><th className="r">Ø CPC</th><th className="r">Conv.</th>
            <th className="r">Kosten/Conv.</th><th className="r">Conv.-Rate</th><th className="r">Gebot</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((r, i) => (
            <tr key={i} className={(r.typ === 'Monat' ? 'gk-monat' : '') + (sel[rowKey(r)] ? ' gk-selected' : '')}>
              <td className="gk-chkcol"><input type="checkbox" checked={!!sel[rowKey(r)]} onChange={() => toggleRow(rowKey(r))} /></td>
              <td><span className={'gk-badge ' + (r.typ === 'Monat' ? 'gk-b-mon' : 'gk-b-wk')}>{r.typ}</span></td>
              <td className="gk-nowrap">{r.zeitraum}</td>
              <td><strong>{r.campaign}</strong></td>
              <td className="gk-nowrap">{r.age}</td>
              <td className="r">{eur(r.costs)}</td>
              <td className="r">{num(r.impr)}</td>
              <td className="r">{num(r.clicks)}</td>
              <td className="r">{pct(r.ctr)}</td>
              <td className="r">{eur(r.cpc)}</td>
              <td className="r">{num(r.conv)}</td>
              <td className="r">{eur(r.cpa)}</td>
              <td className="r">{pct(r.convrate)}</td>
              <td className="r">{gb(r.gebot)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="gk-note">
        Altersdemografie je Kampagne — nur aktive Altersgruppen mit Traffic (0 Impressionen / ausgeschlossen → keine Zeile), Wochen (Fr–Do) + Monate ab 01.01.2026. Additive Werte aus der Tagestabelle aggregiert, Verhältnisse daraus neu gerechnet; <strong>Gebot</strong> = aktuelle Alters-Gebotsanpassung („—" = keine). Anteilsmetriken (IS/Lost-Rang/exact-match) liefert die GAds-API auf Altersebene nicht — daher hier nicht enthalten (Kampagnen-IS siehe B01/B02).
      </div>
    </div>
    </>
  );
}

// ---------- Gruppierter Breakdown (B05/B06): je (Periode × Kampagne) einklappbar ----------
// Kopfzeile = Summe der Gruppe (= Wochen-/Monats-KPI der Kampagne), aufklappen zeigt die Segment-Zeilen.
const XF = { eur, pct, num, text: v => (v == null || v === '' ? '—' : String(v)) };
function GroupedReport({ selectedK, url, segCols, csvName, unit, hint, note, shares = false, gebot = true, extraCols = [] }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState({});
  const [openG, setOpenG] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('token') || '';
    fetch(`${API_BASE}/${url}`, { headers: { 'Authorization': 'Bearer ' + token } })
      .then(r => r.json())
      .then(j => { if (j.success) setRows(j.rows); else setErr(j.message || 'Fehler'); })
      .catch(e => setErr(String(e)));
  }, [url]);

  if (err) return <div className="gk-err">Fehler: {err}</div>;
  if (!rows) return <div className="gk-placeholder">lädt…</div>;
  const shown = rows.filter(r => !selectedK || selectedK[r.campaign]);
  if (!shown.length) return <div className="gk-placeholder">keine Kampagne ausgewählt (oder keine Daten)</div>;

  const segVal = r => segCols.map(c => r[c.key]).join(' · ');
  const rowKey = r => `${r.typ}|${r.zeitraum}|${r.campaign}|${segVal(r)}`;
  // Gruppieren nach (Typ × Zeitraum × Kampagne) — Reihenfolge aus der bereits sortierten Liste
  const groups = []; const gmap = {};
  shown.forEach(r => {
    const gk = `${r.typ}|${r.zeitraum}|${r.campaign}`;
    if (!gmap[gk]) { gmap[gk] = { key: gk, typ: r.typ, zeitraum: r.zeitraum, campaign: r.campaign, rows: [] }; groups.push(gmap[gk]); }
    gmap[gk].rows.push(r);
  });
  const agg = grp => {
    let costs = 0, impr = 0, clicks = 0, conv = 0;
    grp.rows.forEach(r => { costs += r.costs || 0; impr += r.impr || 0; clicks += r.clicks || 0; conv += r.conv || 0; });
    return {
      costs: Math.round(costs * 100) / 100, impr, clicks, conv,
      ctr: impr ? Math.round((clicks / impr) * 10000) / 100 : 0,
      cpc: clicks ? Math.round((costs / clicks) * 100) / 100 : 0,
      cpa: conv ? Math.round((costs / conv) * 100) / 100 : null,
      convrate: clicks ? Math.round((conv / clicks) * 10000) / 100 : 0,
    };
  };

  const toggleG = k => setOpenG(o => ({ ...o, [k]: !o[k] }));
  const setAllG = v => setOpenG(v ? Object.fromEntries(groups.map(g => [g.key, true])) : {});
  const toggleRow = k => setSel(s => ({ ...s, [k]: !s[k] }));
  const groupChecked = g => g.rows.every(r => sel[rowKey(r)]);
  const toggleGroupSel = g => { const v = !groupChecked(g); setSel(s => { const n = { ...s }; g.rows.forEach(r => { n[rowKey(r)] = v; }); return n; }); };
  const pickedCount = shown.filter(r => sel[rowKey(r)]).length;

  const exportCsv = () => {
    const cols = [
      ['Typ', 'typ', 0], ['Zeitraum', 'zeitraum', 0], ['Kampagne', 'campaign', 0],
      ...segCols.map(c => [c.label, c.key, 0]),
      ['Kosten', 'costs', 1], ['Impressionen', 'impr', 1], ['Klicks', 'clicks', 1], ['CTR %', 'ctr', 1], ['Ø CPC', 'cpc', 1],
      ['Conversions', 'conv', 1], ['Kosten/Conv.', 'cpa', 1], ['Conv.-Rate %', 'convrate', 1],
      ...(shares ? [['oberste %', 'oberste', 1], ['obere %', 'obere', 1]] : []),
      ...(gebot ? [['Gebotsanp.', 'gebot', 0]] : []),
      ...extraCols.map(c => [c.label, c.key, c.t === 'text' ? 0 : 1]),
    ];
    const picked = shown.filter(r => sel[rowKey(r)]);
    if (!picked.length) { alert('Keine Zeile ausgewählt.'); return; }
    const cell = (r, c) => {
      if (c[1] === 'gebot') return r.gebot == null ? '' : (r.gebot > 0 ? '+' : '') + r.gebot;
      const v = r[c[1]]; if (v == null) return ''; const s = c[2] ? String(v).replace('.', ',') : String(v); return /[";\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    let csv = '﻿' + cols.map(c => c[0]).join(';') + '\n';
    picked.forEach(r => { csv += cols.map(c => cell(r, c)).join(';') + '\n'; });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = csvName; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
  };

  const nseg = segCols.length;
  return (
    <>
    <div className="gk-toolbar">
      <button className="gk-kbtn" onClick={exportCsv} disabled={!pickedCount}>⬇ CSV-Export{pickedCount ? ` (${pickedCount})` : ''}</button>
      <button className="gk-kbtn" onClick={() => setAllG(true)}>Alle auf</button>
      <button className="gk-kbtn" onClick={() => setAllG(false)}>Alle zu</button>
      <span className="gk-toolbar-hint">{groups.length} Gruppen · {hint}</span>
    </div>
    <div className="gk-tablewrap">
      <table className="gk-table">
        <thead>
          <tr>
            <th className="gk-chkcol"></th>
            <th>Typ</th><th>Zeitraum</th><th>K</th>{segCols.map(c => <th key={c.key}>{c.label}</th>)}
            <th className="r">Kosten</th><th className="r">Impr.</th><th className="r">Klicks</th>
            <th className="r">CTR</th><th className="r">Ø CPC</th><th className="r">Conv.</th>
            <th className="r">Kosten/Conv.</th><th className="r">Conv.-Rate</th>
            {shares && <><th className="r">oberste %</th><th className="r">obere %</th></>}
            {gebot && <th className="r">Gebot</th>}
            {extraCols.map(c => <th key={c.key} className={c.t === 'text' ? '' : 'r'}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {groups.map(g => {
            const a = agg(g); const isOpen = !!openG[g.key];
            return (
              <React.Fragment key={g.key}>
                <tr className="gk-grouphead" onClick={() => toggleG(g.key)}>
                  <td className="gk-chkcol" onClick={e => e.stopPropagation()}><input type="checkbox" checked={groupChecked(g)} onChange={() => toggleGroupSel(g)} title="Gruppe wählen" /></td>
                  <td className="gk-nowrap"><span className="gk-caret">{isOpen ? '▾' : '▸'}</span> <span className={'gk-badge ' + (g.typ === 'Monat' ? 'gk-b-mon' : 'gk-b-wk')}>{g.typ}</span></td>
                  <td className="gk-nowrap">{g.zeitraum}</td>
                  <td><strong>{g.campaign}</strong></td>
                  <td className="gk-muted2" colSpan={nseg}>{g.rows.length} {unit}</td>
                  <td className="r">{eur(a.costs)}</td>
                  <td className="r">{num(a.impr)}</td>
                  <td className="r">{num(a.clicks)}</td>
                  <td className="r">{pct(a.ctr)}</td>
                  <td className="r">{eur(a.cpc)}</td>
                  <td className="r">{num(a.conv)}</td>
                  <td className="r">{eur(a.cpa)}</td>
                  <td className="r">{pct(a.convrate)}</td>
                  {shares && <><td className="r"></td><td className="r"></td></>}
                  {gebot && <td className="r"></td>}
                  {extraCols.map(c => <td key={c.key} className="r"></td>)}
                </tr>
                {isOpen && g.rows.map((r, i) => (
                  <tr key={i} className={'gk-detail' + (sel[rowKey(r)] ? ' gk-selected' : '')}>
                    <td className="gk-chkcol"><input type="checkbox" checked={!!sel[rowKey(r)]} onChange={() => toggleRow(rowKey(r))} /></td>
                    <td></td><td></td><td></td>
                    {segCols.map(c => <td key={c.key} className="gk-nowrap">{r[c.key]}</td>)}
                    <td className="r">{eur(r.costs)}</td>
                    <td className="r">{num(r.impr)}</td>
                    <td className="r">{num(r.clicks)}</td>
                    <td className="r">{pct(r.ctr)}</td>
                    <td className="r">{eur(r.cpc)}</td>
                    <td className="r">{num(r.conv)}</td>
                    <td className="r">{eur(r.cpa)}</td>
                    <td className="r">{pct(r.convrate)}</td>
                    {shares && <><td className="r">{pct(r.oberste)}</td><td className="r">{pct(r.obere)}</td></>}
                    {gebot && <td className="r">{gb(r.gebot)}</td>}
                    {extraCols.map(c => <td key={c.key} className={c.t === 'text' ? 'gk-nowrap' : 'r'}>{XF[c.t](r[c.key])}</td>)}
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      <div className="gk-note">{note}</div>
    </div>
    </>
  );
}

// ---------- B05 · Standorte ----------
function B05Standorte({ selectedK }) {
  return <GroupedReport selectedK={selectedK} url="gads_kpi_b05.php" segCols={[{ label: 'Standort', key: 'standort' }]}
    csvName="gads_b05_standorte.csv" unit="Standorte" hint="Kopf = Wochen-/Monats-Summe, aufklappen für Standort-Details"
    note={<>Standortbericht je Kampagne (Bundesland-Granularität) — je (Woche/Monat × Kampagne) eine einklappbare Gruppe (Kopf = Summe = Wochen-KPI der Kampagne), aufklappen zeigt die Standorte mit Traffic (meiste zuerst). Wochen (Fr–Do) + Monate ab 01.01.2026, additiv aus der Tagestabelle, Verhältnisse neu gerechnet; <strong>Gebot</strong> = aktuelle Standort-Gebotsanpassung. oberste/obere % liefert die GAds-API auf Standortebene nicht.</>} />;
}

// ---------- B06 · Werbezeitplan ----------
function B06Zeitplan({ selectedK }) {
  return <GroupedReport selectedK={selectedK} url="gads_kpi_b06.php" segCols={[{ label: 'Tag', key: 'tag' }, { label: 'Zeit', key: 'zeit' }]}
    csvName="gads_b06_werbezeitplan.csv" unit="Slots" hint="Kopf = Wochen-/Monats-Summe, aufklappen für Tag × Zeitfenster"
    note={<>Werbezeitplan je Kampagne — je (Woche/Monat × Kampagne) eine einklappbare Gruppe (Kopf = Summe = Wochen-KPI der Kampagne), aufklappen zeigt Tag × Zeitfenster (deine 6 Dayparts) mit Traffic, Mo→So. Wochen (Fr–Do) + Monate ab 01.01.2026, aus der stündlichen Tagestabelle aggregiert; <strong>Gebot</strong> = aktuelle Zeitplan-Gebotsanpassung. Conv.-Wert / Alle-Conv. nicht enthalten (= Conversions).</>} />;
}

// ---------- B07 · Zielgruppen ----------
function B07Zielgruppen({ selectedK }) {
  return <GroupedReport selectedK={selectedK} url="gads_kpi_b07.php" segCols={[{ label: 'Segment', key: 'segment' }]}
    csvName="gads_b07_zielgruppen.csv" unit="Segmente" hint="Kopf = Summe der beobachteten Segmente (Klicks>0), aufklappen für Details"
    note={<>Zielgruppensegmente (Beobachtung) je Kampagne — je (Woche/Monat × Kampagne) eine einklappbare Gruppe, aufklappen zeigt die Segmente mit <strong>Klicks &gt; 0</strong> (= CSV-„Gefilterte Zielgruppen"; die Summe ist kleiner als die Kampagne, da Beobachtung nur einen Teil erfasst). Additiv aus der Tagestabelle, <strong>Gebot</strong> = aktuelle Segment-Gebotsanpassung (uservertical-aufgelöst). <em>Namen englisch</em> (API-kanonisch); Typ + Anteilsmetriken (IS/oberste/obere) gibt die GAds-API auf Segmentebene nicht her.</>} />;
}

// ---------- B08 · Suchbegriffe ----------
function B08Suchbegriffe({ selectedK }) {
  return <GroupedReport selectedK={selectedK} url="gads_kpi_b08.php" shares gebot={false}
    segCols={[{ label: 'Suchbegriff', key: 'suchbegriff' }, { label: 'Option', key: 'option' }, { label: 'Keyword', key: 'keyword' }]}
    csvName="gads_b08_suchbegriffe.csv" unit="Suchbegriffe" hint="Status „Keines“ (unbearbeitet), aufklappen für Details"
    note={<>Suchbegriffe je Kampagne — <strong>nur Status „Keines"</strong> (noch nicht als Keyword hinzugefügte / nicht ausgeschlossene Queries = Kandidaten für Negatives &amp; neue Keywords; die bereits verwalteten sind ausgeblendet), wöchentlich (Fr–Do) ab 01.01.2026, einklappbar. Direkt aus <code>search_term_view</code> (echter Match-Type + oberste/obere je Suchbegriff × matched Keyword). Kein Monat (Search-Term-Long-Tail nicht sinnvoll aggregierbar).</>} />;
}

// ---------- B09 · Keywords ----------
function B09Keywords({ selectedK }) {
  return <GroupedReport selectedK={selectedK} url="gads_kpi_b09.php" gebot={false}
    segCols={[{ label: 'Keyword', key: 'keyword' }, { label: 'Option', key: 'option' }]}
    extraCols={[
      { label: 'Max CPC', key: 'maxcpc', t: 'eur' },
      { label: 'QS', key: 'qs', t: 'num' },
      { label: 'Anz.-Rel.', key: 'rel', t: 'text' },
      { label: 'Erw. CTR', key: 'ectr', t: 'text' },
      { label: 'LP-Erf.', key: 'lp', t: 'text' },
      { label: 'Geb. 1.Seite', key: 'bid_first', t: 'eur' },
      { label: 'Geb. oberste', key: 'bid_top', t: 'eur' },
      { label: 'Geb. obere', key: 'bid_tp', t: 'eur' },
    ]}
    csvName="gads_b09_keywords.csv" unit="Keywords" hint="aktive Keywords (inkl. 0 Impr — wegen Max CPC), aufklappen für Details"
    note={<>Keywords je Kampagne — aktive Keywords (Keyword + Anzeigengruppe + Kampagne ENABLED, keine Ausschluss-Keywords), Wochen (Fr–Do) + Monate ab 01.01.2026, einklappbar. <strong>Auch 0-Impressionen-Keywords</strong> (wegen Max CPC). Metriken periodenspezifisch aus <code>keyword_view</code>; <strong>Max CPC · Qualitätsfaktor · Anz.-Rel./Erw. CTR/LP-Erf. · Gesch. Gebote</strong> (1. Seite / oberste / obere Position) = aktuelle Werte (Setting-Charakter).</>} />;
}

// ---------- B10 · Titel (Anzeigentitel) ----------
function B10Titel({ selectedK }) {
  return <GroupedReport selectedK={selectedK} url="gads_kpi_assets.php?type=Titel" gebot={false}
    segCols={[{ label: 'Titel', key: 'asset' }]}
    csvName="gads_b10_titel.csv" unit="Titel" hint="Anzeigentitel je Kampagne, aufklappen für Details"
    note={<>Anzeigentitel (Headlines der responsiven Suchanzeigen) je Kampagne — je (Woche/Monat × Kampagne) eine einklappbare Gruppe, aufklappen zeigt die Titel-Assets (inkl. 0-Impr). Wochen (Fr–Do) + Monate ab 01.01.2026, aus <code>ad_group_ad_asset_view</code> (ENABLED), je Asset über die Anzeigen aggregiert, inkl. Conversions.</>} />;
}

// ---------- B11 · Texte (Textzeilen) ----------
function B11Texte({ selectedK }) {
  return <GroupedReport selectedK={selectedK} url="gads_kpi_assets.php?type=Text" gebot={false}
    segCols={[{ label: 'Text', key: 'asset' }]}
    csvName="gads_b11_texte.csv" unit="Texte" hint="Textzeilen je Kampagne, aufklappen für Details"
    note={<>Textzeilen (Descriptions der responsiven Suchanzeigen) je Kampagne — einklappbar, Wochen (Fr–Do) + Monate ab 01.01.2026, aus <code>ad_group_ad_asset_view</code> (ENABLED), je Asset aggregiert, inkl. Conversions.</>} />;
}

// ---------- B12 · Sitelinks ----------
function B12Sitelinks({ selectedK }) {
  return <GroupedReport selectedK={selectedK} url="gads_kpi_assets.php?type=Sitelink" gebot={false}
    segCols={[{ label: 'Sitelink', key: 'asset' }]}
    csvName="gads_b12_sitelinks.csv" unit="Sitelinks" hint="Sitelinks je Kampagne, aufklappen für Details"
    note={<>Sitelinks je Kampagne — einklappbar, Wochen (Fr–Do) + Monate ab 01.01.2026, aus <code>campaign_asset</code> (field_type SITELINK), je Asset inkl. Conversions.</>} />;
}

// ---------- B12a · Marke (Logo + Firmenname) ----------
function B12aMarke({ selectedK }) {
  return <GroupedReport selectedK={selectedK} url="gads_kpi_assets.php?type=Marke" gebot={false}
    segCols={[{ label: 'Typ', key: 'atyp' }, { label: 'Asset', key: 'asset' }]}
    csvName="gads_b12a_marke.csv" unit="Marken-Assets" hint="Logo + Firmenname je Kampagne, aufklappen für Details"
    note={<>Marken-Assets (Logo + Name des Unternehmens) je Kampagne — einklappbar, aus <code>campaign_asset</code> (BUSINESS_LOGO + BUSINESS_NAME), inkl. Conversions.</>} />;
}

// ---------- B12c · Bilder ----------
function B12cBilder({ selectedK }) {
  return <GroupedReport selectedK={selectedK} url="gads_kpi_assets.php?type=Bild" gebot={false}
    segCols={[{ label: 'Bild', key: 'asset' }]}
    csvName="gads_b12c_bilder.csv" unit="Bilder" hint="Bild-Assets je Kampagne, aufklappen für Details"
    note={<>Bild-Assets (Bild-Erweiterungen) je Kampagne — einklappbar, aus <code>campaign_asset</code> (field_type AD_IMAGE), je Asset inkl. Conversions (Asset-Name statt tpc-URL, wo vorhanden).</>} />;
}
